/**
 * Show More Render (V2)
 * DOM更新のみを担当
 */

import type { ShowMoreForm } from "./show-more-form";
import { bindShowMoreTransitionEnd } from "./show-more-listener";

export class ShowMoreRender {
    public static ensureStructure(form: ShowMoreForm): void {
        const root = form.getRoot();

        let toggle = form.getToggle(root);
        let panel = form.getPanel(root);
        let content = panel ? form.getContent(panel) : null;

        if (!(toggle instanceof HTMLButtonElement)) {
            const createdToggle = document.createElement("button");
            createdToggle.type = "button";
            createdToggle.className = "show-more__toggle";

            const label = document.createElement("span");
            label.className = "show-more__label";
            const icon = document.createElement("span");
            icon.className = "show-more__icon";
            icon.setAttribute("aria-hidden", "true");

            createdToggle.appendChild(label);
            createdToggle.appendChild(icon);
            root.prepend(createdToggle);
            toggle = createdToggle;
        }

        if (!(panel instanceof HTMLDivElement)) {
            panel = document.createElement("div");
            panel.className = "show-more__panel";
            root.appendChild(panel);
        }

        if (!(content instanceof HTMLDivElement)) {
            content = document.createElement("div");
            content.className = "show-more__content";
            panel.appendChild(content);
        }

        const movableNodes: ChildNode[] = [];
        root.childNodes.forEach((node) => {
            if (node === toggle || node === panel) return;
            movableNodes.push(node);
        });
        movableNodes.forEach((node) => content.appendChild(node));

        form.toggleButton = toggle instanceof HTMLButtonElement ? toggle : null;
        form.panelElement = panel instanceof HTMLDivElement ? panel : null;
        form.contentElement = content instanceof HTMLDivElement ? content : null;
        const labelElement = form.getLabel(toggle);
        form.labelElement = labelElement instanceof HTMLElement ? labelElement : null;
    }

    public static applyDuration(form: ShowMoreForm, durationMs: number): void {
        form.panelElement?.style.setProperty("--show-more-duration", `${durationMs}ms`);
    }

    public static applyLines(form: ShowMoreForm, lines: number): void {
        const root = form.getRoot();
        const content = form.contentElement;

        root.classList.toggle("show-more--clamp", lines > 0);
        if (content instanceof HTMLDivElement) {
            content.style.setProperty("--show-more-lines", `${Math.max(1, lines)}`);
        }
    }

    public static renderLabel(form: ShowMoreForm, text: string): void {
        if (form.labelElement instanceof HTMLElement) {
            form.labelElement.textContent = text;
        }
    }

    public static renderAria(form: ShowMoreForm, expanded: boolean): void {
        if (form.toggleButton instanceof HTMLButtonElement) {
            form.toggleButton.setAttribute("aria-expanded", expanded ? "true" : "false");
        }
    }

    public static renderRootState(form: ShowMoreForm, expanded: boolean): void {
        const root = form.getRoot();
        root.classList.toggle("is-open", expanded);
    }

    public static renderClampState(form: ShowMoreForm, expanded: boolean): void {
        const content = form.contentElement;
        if (!(content instanceof HTMLDivElement)) return;
        content.classList.toggle("is-clamped-lines", !expanded);
    }

    public static renderToggleVisibility(form: ShowMoreForm, visible: boolean): void {
        const toggle = form.toggleButton;
        if (!(toggle instanceof HTMLButtonElement)) return;
        toggle.hidden = !visible;
    }

    public static hasClampOverflow(form: ShowMoreForm, lines: number): boolean {
        const content = form.contentElement;
        if (!(content instanceof HTMLDivElement)) return false;
        if (lines <= 0) return false;

        const width = content.getBoundingClientRect().width;
        if (!Number.isFinite(width) || width <= 0) return false;

        const textBlock = content.firstElementChild instanceof HTMLElement ? content.firstElementChild : content;
        const style = globalThis.getComputedStyle(textBlock);

        const lineHeightRaw = Number.parseFloat(style.lineHeight || "");
        const fontSizeRaw = Number.parseFloat(style.fontSize || "");
        const lineHeight =
            Number.isFinite(lineHeightRaw) && lineHeightRaw > 0
                ? lineHeightRaw
                : (Number.isFinite(fontSizeRaw) && fontSizeRaw > 0 ? fontSizeRaw : 16) * 1.5;

        const estimatedLines = textBlock.scrollHeight / lineHeight;
        return estimatedLines > Math.max(1, lines) + 0.15;
    }

    public static renderPanel(
        form: ShowMoreForm,
        expanded: boolean,
        animate: boolean,
        prefersReducedMotion: boolean,
    ): void {
        const panel = form.panelElement;
        const content = form.contentElement;
        if (!(panel instanceof HTMLDivElement) || !(content instanceof HTMLDivElement)) return;

        const shouldAnimate = !!animate && !prefersReducedMotion;

        if (!shouldAnimate) {
            panel.hidden = !expanded;
            panel.style.maxHeight = expanded ? "none" : "0px";
            return;
        }

        if (expanded) {
            panel.hidden = false;
            panel.style.maxHeight = "0px";

            requestAnimationFrame(() => {
                panel.style.maxHeight = `${content.scrollHeight}px`;
            });
            return;
        }

        panel.style.maxHeight = `${content.scrollHeight}px`;
        requestAnimationFrame(() => {
            panel.style.maxHeight = "0px";
        });
    }

    public static renderClampPanel(
        form: ShowMoreForm,
        expanded: boolean,
        animate: boolean,
        prefersReducedMotion: boolean,
        onComplete?: () => void,
    ): void {
        const panel = form.panelElement;
        const content = form.contentElement;
        if (!(panel instanceof HTMLDivElement) || !(content instanceof HTMLDivElement)) return;

        panel.hidden = false;

        const shouldAnimate = !!animate && !prefersReducedMotion;
        const fromHeight = content.getBoundingClientRect().height;

        const hadClampClass = content.classList.contains("is-clamped-lines");
        let toHeight = fromHeight;

        if (expanded) {
            if (hadClampClass) {
                content.classList.remove("is-clamped-lines");
                toHeight = content.getBoundingClientRect().height;
                content.classList.add("is-clamped-lines");
            }
            content.classList.remove("is-clamped-lines");
        } else if (!hadClampClass) {
            content.classList.add("is-clamped-lines");
            toHeight = content.getBoundingClientRect().height;
            content.classList.remove("is-clamped-lines");
        }

        if (!shouldAnimate) {
            content.classList.toggle("is-clamped-lines", !expanded);
            panel.style.maxHeight = "none";
            onComplete?.();
            return;
        }

        panel.style.maxHeight = `${Math.max(0, Math.ceil(fromHeight))}px`;
        void panel.offsetHeight;

        requestAnimationFrame(() => {
            panel.style.maxHeight = `${Math.max(0, Math.ceil(toHeight))}px`;
        });

        const onEnd = () => {
            content.classList.toggle("is-clamped-lines", !expanded);
            panel.style.maxHeight = "none";
            onComplete?.();
        };
        bindShowMoreTransitionEnd(panel, onEnd);
    }
}
